import type { AutoGroupColumnDef, GroupCellRendererParams } from 'ag-grid-community';
import { Text } from '../../Text';
import { DEFAULT_GROUP_CELL_RENDERER_PARAMS } from '../defaultGroupCellRendererParams';

/**
 * When you are building a tree blotter, you need a different group column than if you're building a standard row grouping blotter.
 *
 * A row grouping blotter can figure out exactly what entity / column is being used to perform grouping and grab formatting and filtering logic
 * from the correct columns. A tree blotter cannot do this, and thus the implementer of new tree blotters has to apply manual logic
 * (overriding on top of this base object) to perform the necessary functions.
 *
 * Lastly, this column definition is not a factory like the other surrounding column definitions. Group columns are still handled
 * differently by useBlotterTable in terms of being passed as a parameter.
 */
export const baseTreeGroupColumnDef = {
  cellRenderer: 'agGroupCellRenderer',
  cellRendererParams: {
    ...DEFAULT_GROUP_CELL_RENDERER_PARAMS,
    suppressCount: true,
  },
  cellRendererSelector: params => {
    if (!params.node.rowPinned) {
      return undefined;
    }
    // Pinned rows (e.g. totals) opt out of the default group cell renderer and show plain text.
    return {
      params,
      component: (params: GroupCellRendererParams) => <Text>{params.data.groupColumnValue ?? params.value}</Text>,
    };
  },
  headerName: 'Group',
  editable: false,
  suppressColumnsToolPanel: false,
  sortable: true,
} satisfies AutoGroupColumnDef;
